<script setup lang="ts">
import { ref, watch } from "vue";
import type { Emits } from ".";
import { generalLogic } from "@/global";
import { useI18n } from "vue-i18n";
import { userAnalyticsService } from "@/services";

const { t } = useI18n();
const { debounce } = generalLogic;

const emit = defineEmits<Emits>();

const props = defineProps({
  searchText: {
    type: String || null,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: ""
  }
});

const searchText = ref(props.searchText);

watch(
  () => props.searchText,
  (newValue) => {
    if (newValue && newValue.trim().length > 0) {
      searchText.value = newValue?.trim();
    } else {
      searchText.value = null;
    }
  },
  { immediate: true }
);

const emitSearchText = () => {
  emit("enter", searchText.value);
};

const debouncedSearch = debounce(emitSearchText);

const onInput = (searchTerm: string) => {
  searchText.value = searchTerm?.trim();
  debouncedSearch();
  if (!searchText.value) {
    emit("clear");
  }
};

const onSearchClick = () => {
  if (searchText.value && searchText.value.trim().length > 0) {
    userAnalyticsService.trackEvent("Search: Executed", {
      page: "search",
      searchTerm: searchText.value.trim()
    });
    emit("searchClick", searchText.value.trim());
  }
};

const onClearButtonClick = () => {
  userAnalyticsService.trackEvent("Search: Cleared", {
    page: "search"
  });
  searchText.value = null;
  emit("clearButtonClick");
};
</script>

<template>
  <mc-input
    v-web-component-fix
    data-test="header/search-box"
    :placeholder="placeholder || t('SEARCH')"
    trailingicon="magnifying-glass"
    clickabletrailingicon
    :loading="loading"
    fit="small"
    :hiddenlabel="true"
    :value="searchText || ''"
    clearbutton
    keepclearbuttonvisible
    class="search-bar-input"
    @input.prevent="onInput($event.target.value)"
    @clearbuttonclick.prevent="onClearButtonClick"
    @trailingiconclick.prevent="onSearchClick"
    @keydown.enter="onSearchClick"
  >
    <span slot="errormessage" data-test="header/search-box-error">{{ t("PLEASE_ENTER_AT_LEAST_3_CHARACTERS") }}</span>
  </mc-input>
</template>

<style scoped lang="scss">
.search-bar-input {
  width: 100%;
}
</style>
